import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { ReactComponent as PackageIcon } from "assets/images/iconsV2/package.svg";
import { ReactComponent as VolumeCubicMeterIcon } from "assets/images/iconsV2/volume-cubic-meter.svg";
import { ReactComponent as WeightIcon } from "assets/images/iconsV2/weight.svg";
import usePermissions from "hooks/usePermissions";
import { useUnitSystemPreferences } from "@RequestFormV2/hooks/useUnitSystemPreferences";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import {
  calculatePackingListTotals,
  formatPackingListCbm,
  formatPackingListWeight,
} from "@RequestFormV2/sections/PackingList/utils/packingListCalculations";

type ReviewPackingListSummaryProps = {
  packingList: PackingListRow[];
};

export default function ReviewPackingListSummary({ packingList }: ReviewPackingListSummaryProps) {
  const intl = useIntl();
  const { parsedPermissions } = usePermissions();
  const { weightUnit } = useUnitSystemPreferences();
  const goodsConfirmationType = useWatch({ name: "organizational.goods_confirmation_type" }) as string | undefined;

  const companySettings = parsedPermissions.company_shipper_settings ?? {};
  const showGoodsConfirmation = companySettings.goods_list_estimation;

  const totals = useMemo(() => calculatePackingListTotals(packingList, weightUnit), [packingList, weightUnit]);

  const goodsConfirmationLabel = useMemo(() => {
    if (goodsConfirmationType === "Finalized") {
      return intl.formatMessage({
        id: "request-form.packing-list.goodsAreFinalized",
        defaultMessage: "Goods are finalized",
      });
    }

    return intl.formatMessage({
      id: "request-form.packing-list.goodsAreEstimated",
      defaultMessage: "Goods are estimated",
    });
  }, [intl, goodsConfirmationType]);

  return (
    <div className="rfv2-review-packing-list__summary">
      <div className="rfv2-review-packing-list__summary-stats">
        <div className="rfv2-review-packing-list__summary-stat">
          <div className="rfv2-review-packing-list__summary-label-group">
            <WeightIcon />
            <span>
              {intl.formatMessage({
                id: "request-form.review.packingList.totalWeight",
                defaultMessage: "Total weight",
              })}
            </span>
          </div>
          <strong>
            {formatPackingListWeight(totals.totalWeight)} {weightUnit}
          </strong>
        </div>

        <div className="rfv2-review-packing-list__summary-stat">
          <div className="rfv2-review-packing-list__summary-label-group">
            <VolumeCubicMeterIcon />
            <span>
              {intl.formatMessage({
                id: "request-form.review.packingList.totalCbm",
                defaultMessage: "Total CBM",
              })}
            </span>
          </div>
          <strong>
            {formatPackingListCbm(totals.totalCBM)} m<sup>3</sup>
          </strong>
        </div>

        <div className="rfv2-review-packing-list__summary-stat">
          <div className="rfv2-review-packing-list__summary-label-group">
            <PackageIcon />
            <span>
              {intl.formatMessage({
                id: "request-form.review.packingList.totalUnits",
                defaultMessage: "Total units",
              })}
            </span>
          </div>
          <strong>{totals.totalUnits}</strong>
        </div>
      </div>

      {showGoodsConfirmation && (
        <span className="rfv2-review-packing-list__goods-confirmation">{goodsConfirmationLabel}</span>
      )}
    </div>
  );
}
